import moment from 'moment';
import React, {useContext} from 'react';
import {observer} from 'mobx-react';
import {ThemeContext} from '../../apiContexts';
import RoundButton from '../../components/roundButton';
import {convertSpeedTo, nomenclatureSpeedOf} from '../../helpers/conversions';
import DeviceFooter from '../personal/device/device-detail/device-footer';
import {durationFormat} from '../../helpers/date';
import Velocity from '../shared/velocity';
import './device-big-fleet.sass';
import I18nMessage from '../../components/I18nMessage';
import CommandConfirmationModal from '../modals/CommandConfirmationModal';
import {useDeviceDetails} from 'src/autobahn/features/devices/queries';
import useAlerts from 'src/hooks/use-alerts';
import {useIntl} from 'react-intl';


const DeviceBigFleet = (props) => {
    const {toast} = useAlerts();
    const intl = useIntl();
    const {selectedDevice} = props.devices;

    // enable: false will prevent fetching on mount
    const {refetch: getDeviceDetails, isFetching: isFetchingTag} = useDeviceDetails(selectedDevice?.id, {enabled: false});

    const getTagPosition = async () => {
        // cancelRefetch will prevent new requests until the current one is finished
        const {isSuccess, isError, error} = await getDeviceDetails({cancelRefetch: false});
        if (isSuccess) {
            return toast.success(intl.formatMessage({id: 'devicePositionUpdateSuccess'}));
        }
        if (isError) {
            return toast.errorStatus(error, {legacy: false});
        }
    };

    return (
        <div id="deviceBigFleet">
            <Header {...selectedDevice} server={props.server}/>
            <div className={'location-info'}>
                <Location currentPosition={selectedDevice.currentPosition}/>
                <Driver currentPosition={selectedDevice.currentPosition} drivers={props.drivers}/>
            </div>

            {/* <Pictures /> */}
            <DeviceInfo {...selectedDevice} />
            <DeviceActions {...props} isTag={selectedDevice.isTag()} getTagPosition={getTagPosition} isFetchingTag={isFetchingTag} />
            <CommandConfirmationModal
                theme={props.theme}
                name={selectedDevice.name}
                isOpenModalCommand={props.isOpenModalCommand}
                toggleModalCommand={props.toggleModalCommand}
                block={props.block}
                sendCommand={props.sendCommand}
                commandData={props.commandData}
            />
        </div>
    );
};

const Header = observer(({
                    currentPosition = {},
                    name,
                    uniqueId,
                    server,
                }) => {
    const intl = useIntl();
    const theme = useContext(ThemeContext);

    return (
        <div
            id="header"
            className="d-flex flex-column align-items-start justify-content-center"
            style={{backgroundColor: theme}}>
            <h4 className="text-truncate text-left">{name}</h4>
            <small>ID: {uniqueId}</small>
                <small>
                    {(currentPosition?.latitude !== undefined && currentPosition?.longitude !== undefined) ?
                        `${currentPosition?.latitude}º, ${currentPosition?.longitude}º` :
                        intl.formatMessage({id: 'notAvailable'})
                    }
                </small>
            <Velocity
                speed={convertSpeedTo(
                    currentPosition?.speed,
                    server.attributes && server.attributes.speedUnit,
                )}
                limit={
                    server &&
                    server.attributes &&
                    convertSpeedTo(server.attributes.speedLimit, server.attributes.speedUnit)
                }
                unit={nomenclatureSpeedOf(
                    server.attributes && server.attributes.speedUnit,
                )}
            />
        </div>
    );
});

const Location = observer(({currentPosition}) => {
    const intl = useIntl();
    const theme = useContext(ThemeContext);

    return (
        <div id="location" className="mt-4">
            <strong className="mb-1" style={{color: theme}}>
                <I18nMessage id="deviceLastUpdate"/>
            </strong>
            <span className="text-gray">
        {currentPosition && currentPosition.fixTime ?
            moment(currentPosition.fixTime).format('YYYY-MM-DD HH:mm:ss') :
            intl.formatMessage({id: 'notAvailable'})}
      </span>
            <span className="text-gray">
        {intl.formatMessage({id: 'deviceStoppedFor'})}: {currentPosition?.attributes?.stoppedTime != null ?
            durationFormat(currentPosition.attributes.stoppedTime, 'HH:mm:ss') :
            intl.formatMessage({id: 'notAvailable'})}
      </span>
            <strong className="mt-3 mb-1" style={{color: theme}}>
                <I18nMessage id="sharedAddress"/>
            </strong>
            <span className="text-gray">
        {currentPosition && currentPosition.address ?
            currentPosition.address :
            intl.formatMessage({id: 'notAvailable'})}
      </span>
        </div>
    );
});

const Driver = observer(({currentPosition, drivers}) => {
    const intl = useIntl();
    const theme = useContext(ThemeContext);

    const driver = currentPosition && currentPosition.attributes ?
        drivers.getDriverByUniqueId(currentPosition.attributes.driverUniqueId) : null;

    return (
        <div id="driver" className="mt-3">
            <div>
                <strong className="mb-1" style={{color: theme}}>
                    <I18nMessage id="sharedDriver"/>
                </strong>
                <span className="text-gray">{driver ? driver.name : intl.formatMessage({id: 'notAvailable'})}</span>
            </div>
            <img
                src={`${process.env.PUBLIC_URL}/images/default.jpg`}
                alt="profile"
                className="rounded-circle"
            />
        </div>
    );
});

// const Pictures = () => {
//   const theme = useContext(ThemeContext);

//   return (
//     <div id="picture" className="mt-3">
//       <strong className="mb-1" style={{color: theme}}>
//         Ver detalhes do dispositivo
//       </strong>
//       <UncontrolledCarousel
//         autoPlay={false}
//         items={[
//           {
//             src: `${process.env.PUBLIC_URL}/images/noImgDefault.jpg?1`,
//             caption: '',
//           },
//           {
//             src: `${process.env.PUBLIC_URL}/images/noImgDefault.jpg?2`,
//             caption: '',
//           },
//           {
//             src: `${process.env.PUBLIC_URL}/images/noImgDefault.jpg?3`,
//             caption: '',
//           },
//         ]}
//       />
//     </div>
//   );
// };

const DeviceInfo = (props) => (
    <div id="deviceInfo" className="mt-3">
        <span className="text-gray"><I18nMessage id="personalDeviceInfo"/></span>
        <DeviceFooter {...props} />
    </div>
);

const DeviceActions = (props) => {
    const {
        trace,
        traceToggle,
        followToogle,
        follow,
        createGeofence,
        block,
        close,
        getTagPosition,
        isFetchingTag,
        isTag,
    } = props;
    const intl = useIntl();
    const theme = useContext(ThemeContext);

    return (
        <div id="deviceActions" className="pb-2">
            <strong className="" style={{color: theme}}>
                <I18nMessage id="deviceActionsTitle"/>
            </strong>
            <div className="row d-flex justify-content-center align-items-start">
                <RoundButton
                    withBorder
                    noBgBadge
                    labelDirection="bottom"
                    label={intl.formatMessage({id: 'deviceRoute'})}
                    className="mx-72px"
                    active={trace}
                    onClick={traceToggle}>
                    <span className="fal fa-route"/>
                </RoundButton>

                <RoundButton
                    withBorder
                    noBgBadge
                    labelDirection="bottom"
                    onClick={() => block(true)}
                    className="mx-72px"
                    label={intl.formatMessage({id: 'deviceBlock'})}>
                    <span className="fas fa-lock-alt text-danger"/>
                </RoundButton>

                <RoundButton
                    labelDirection="bottom"
                    withBorder
                    noBgBadge
                    className="mx-72px"
                    onClick={() => block(false)}
                    label={intl.formatMessage({id: 'deviceRelease'})}>
                    <span className="fas fa-unlock-alt text-green"/>
                </RoundButton>

                <RoundButton
                    withBorder
                    noBgBadge
                    labelDirection="bottom"
                    label={intl.formatMessage({id: 'deviceFollow'})}
                    active={follow}
                    className="mx-72px"
                    onClick={followToogle}>
                    <span className="fas fa-location-arrow"/>
                </RoundButton>

                <RoundButton
                    withBorder
                    noBgBadge
                    labelDirection="bottom"
                    className="mx-72px"
                    onClick={createGeofence}
                    label={intl.formatMessage({id: 'deviceFence'})}>
                      <span style={{fontSize: '2rem'}} className="fal fa-location-circle fa-2x" />
                </RoundButton>

                {isTag && (
                    <RoundButton
                        withBorder
                        noBgBadge
                        labelDirection="bottom"
                        onClick={getTagPosition}
                        disabled={isFetchingTag}
                        className="mx-72px"
                        textWrap
                        label={intl.formatMessage({id: 'actionUpdatePosition'})}>
                        <span style={{fontSize: '2rem'}} className="fal fa-compass fa-2x" />
                    </RoundButton>
                )}

                <RoundButton
                    withBorder
                    noBgBadge
                    labelDirection="bottom"
                    label={intl.formatMessage({id: 'sharedClose'})}
                    className="mx-72px"
                    onClick={close}>
                    <span className="fas fa-times text-secondary"/>
                </RoundButton>
            </div>
        </div>
    );
};


export default observer(DeviceBigFleet);
